import React, {useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../core/services/api/api";
import {Col} from "reactstrap";
import ProductTechnicalFileCard from "../../../../../../components/app-specific/product-cards/product-technical-file-card";
import TryAgain from "../../../../../../components/app-specific/try-again";
import ProductInformationTechnicalFilesFormView from "./form";
import {useParams} from "react-router-dom";


const ProductInformationTechnicalFilesView = ({loading: loadingProps, product}) => {
    const params = useParams()
    const [loadingTechnicalFiles, setLoadingTechnicalFiles] = useState(true);
    const [technicalFiles, setTechnicalFiles] = useState([]);
    const isMounted = useIsMounted();

    const loading = useMemo(() => loadingProps || loadingTechnicalFiles, [loadingProps, loadingTechnicalFiles])

    /**
     * With each change in id of the url params:
     * - fetches the product technical files from the server.
     */
    useEffect(() => {
        getProductTechnicalFiles().then();
    }, [params?.id])

    /**
     * Fetches the technical files of the current product from the server.
     * @return {Promise<void>}
     */
    const getProductTechnicalFiles = async () => {
        setLoadingTechnicalFiles(true);
        const response = await Api.getOperatorPartTechnicalFiles(params.id);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setTechnicalFiles(response?.data ?? []);
        }
        setLoadingTechnicalFiles(false);
    }

    /**
     * Updates the given technical files' information in the server.
     * @param technicalFile
     * @param values
     * @return {Promise<void>}
     */
    const updateProductTechnicalFile = async (technicalFile, values) => {
        const forApi = {
            id: technicalFile.id,
            name: values.name,
            description: values.description,
        }
        const response = await Api.updateOperatorPartTechnicalFile(forApi);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setTechnicalFiles(prevState => prevState.map(e => e.id === forApi.id
                ? {...e, ...forApi}
                : e
            ))
        }
        return response;
    }

    /**
     * Removes the given technical file from the server.
     * @param technicalFile
     * @return {Promise<void>}
     */
    const removeProductTechnicalFile = async (technicalFile) => {
        const response = await Api.removeOperatorPartTechnicalFile(technicalFile.id);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setTechnicalFiles(prevState => prevState?.filter(e => e.id !== technicalFile.id))
        }
    }

    /**
     * Sets the given version as the current version of the given technical file (by merging them).
     * @param technicalFile
     * @param version
     */
    const setTechnicalFileVersion = (technicalFile, version) => {
        setTechnicalFiles(prevState => prevState.map(e => e.id === technicalFile.id
            ? {...e, createDate: version.createDate, fileAddress: version.fileAddress}
            : e
        ))
    }

    return (
        <>
            <div className={'product-technical-files-view part-info-paper'}>
                <Col xs={12} lg={6} className={'d-flex flex-column'}>
                    {
                        loading
                            ? Array(4).fill(null).map((_, e) => (
                                <div key={e} className={'w-100 loading extraLarge mb-3'}>
                                    <div/>
                                </div>
                            ))
                            : technicalFiles?.length > 0
                                ? technicalFiles?.map(technicalFile => (
                                    <ProductTechnicalFileCard
                                        key={technicalFile.id}
                                        technicalFile={technicalFile}
                                        setTechnicalFileVersion={setTechnicalFileVersion}
                                        setTechnicalFiles={setTechnicalFiles}
                                        remove={removeProductTechnicalFile}
                                        update={updateProductTechnicalFile}
                                        onAllVersionsRemoved={(t) => setTechnicalFiles(prevState => prevState.filter(e => e.id !== t.id))}
                                        partNo={product?.partNo}
                                        industryId={product?.industryId}
                                    />
                                ))
                                : <div className={'my-5 py-5'}>
                                    <TryAgain
                                        text={'There are currently no technical files submitted for this product. you may' +
                                            ' add one using the provided form'}
                                    />
                                </div>
                    }
                </Col>
                <Col xs={12} lg={6}>
                    <ProductInformationTechnicalFilesFormView
                        onSuccess={(f) => setTechnicalFiles(prevState => [...prevState, f])}
                    />
                </Col>
            </div>
        </>
    )
}


export default ProductInformationTechnicalFilesView;
